import React, { useState } from 'react';
import { Plus, Mail, Phone, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useJobContacts } from '../../hooks/useJobContacts';
import AddJobContactPopup from './contacts/AddJobContactPopup';
import ConfirmationPopup from '../ui/ConfirmationPopup';
import type { ContactRole } from '../../types/job';

interface JobContactsProps {
  jobId: string;
  companyId: string;
}

export default function JobContacts({ jobId, companyId }: JobContactsProps) {
  const { contacts, loading, error, refetch, removeContact } = useJobContacts(jobId);
  const [showAddContact, setShowAddContact] = useState(false);
  const [contactToRemove, setContactToRemove] = useState<string | null>(null);

  const handleRemoveContact = async () => {
    if (!contactToRemove) return;
    
    const { error: removeError } = await removeContact(contactToRemove);
    if (!removeError) {
      refetch();
    }
    setContactToRemove(null);
  };

  if (loading) {
    return <div className="text-gray-400">Loading contacts...</div>;
  }

  if (error) {
    return <div className="text-red-500">{error}</div>;
  }

  const roleLabels: Record<ContactRole, string> = {
    interviewer: 'Interviewer',
    hiring_manager: 'Hiring Manager',
    recruiter: 'Recruiter',
    referral: 'Referral'
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Company Contacts ({contacts.length})</h3>
        <button 
          onClick={() => setShowAddContact(true)}
          className="btn btn-secondary flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Contact
        </button>
      </div>

      {contacts.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          No contacts added yet
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {contacts.map((jobContact) => (
            <div key={jobContact.id} className="card p-4 space-y-2">
              <div className="flex justify-between items-start">
                <div>
                  <Link 
                    to={`/contacts/${jobContact.contact?.id}`}
                    className="font-medium text-primary-500 hover:text-primary-400"
                  >
                    {jobContact.contact?.first_name} {jobContact.contact?.last_name}
                  </Link>
                  <p className="text-sm text-gray-400">
                    {roleLabels[jobContact.role]}
                    {jobContact.contact?.job_title && ` • ${jobContact.contact.job_title}`}
                  </p>
                </div>
                <button
                  onClick={() => setContactToRemove(jobContact.contact?.id)}
                  className="text-gray-400 hover:text-red-500 p-1"
                  title="Remove contact"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              <div className="space-y-1">
                {jobContact.contact?.work_email && (
                  <div className="flex items-center text-sm text-gray-400">
                    <Mail className="w-4 h-4 mr-2" />
                    <a 
                      href={`mailto:${jobContact.contact.work_email}`}
                      className="hover:text-primary-500"
                    >
                      {jobContact.contact.work_email}
                    </a>
                  </div>
                )}
                {jobContact.contact?.work_phone && (
                  <div className="flex items-center text-sm text-gray-400">
                    <Phone className="w-4 h-4 mr-2" />
                    <a 
                      href={`tel:${jobContact.contact.work_phone}`}
                      className="hover:text-primary-500"
                    >
                      {jobContact.contact.work_phone}
                    </a>
                  </div>
                )}
              </div>

              {jobContact.notes && (
                <p className="text-sm text-gray-400 mt-2">
                  {jobContact.notes}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {showAddContact && (
        <AddJobContactPopup
          jobId={jobId}
          companyId={companyId}
          onClose={() => setShowAddContact(false)}
          onSuccess={() => {
            refetch();
            setShowAddContact(false);
          }}
        />
      )}

      {contactToRemove && (
        <ConfirmationPopup
          title="Remove Contact"
          message="Are you sure you want to remove this contact from the job? This action cannot be undone."
          confirmLabel="Remove"
          onConfirm={handleRemoveContact}
          onCancel={() => setContactToRemove(null)}
        />
      )}
    </div>
  );
}